import React, { useState, useEffect, useCallback } from 'react';
import {
    BanknotesIcon,
    ClockIcon,
    FunnelIcon,
    CheckCircleIcon,
    SparklesIcon,
    ArrowPathIcon
} from '@heroicons/react/24/outline';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import LayoutWrapper from '../components/LayoutWrapper';
import Helper from '../helpers';
import config from '../config';

const baseUrl = config.apiBaseUrl;

// Token History Category enum values (matching backend)
const TokenHistoryCategory = {
    Purchase: 1,
    SessionBooking: 2,
    SessionCancellation: 3,
    ManualAdjustment: 4,
    SubscriptionAllocation: 5,
    Refund: 6
};

// Category color mappings - Premium color scheme
const categoryColors = {
    // Token categories
    Purchase: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    SessionBooking: 'bg-sky-500/20 text-sky-400 border-sky-500/30',
    SessionCancellation: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    SubscriptionAllocation: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
    ManualAdjustment: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
    Refund: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
};

const TransactionHistory = () => {
    const [currentVenueKey, setCurrentVenueKey] = useState("");
    const [loading, setLoading] = useState(true);
    const [tokenHistory, setTokenHistory] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    
    // Filter states
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [fromDate, setFromDate] = useState(dayjs().subtract(30, 'day'));
    const [toDate, setToDate] = useState(dayjs());
    const [showFilters, setShowFilters] = useState(false);

    // Get unique categories from data
    const categories = [...new Set(tokenHistory.map(t => t.categoryName))];

    const fetchTokenHistory = useCallback( async () => {
        setLoading(true);
        try {
            const token = await Helper.getCookie('wh-jwt');
            const params = new URLSearchParams();
            if (fromDate) params.append('from', fromDate.toISOString());
            if (toDate) params.append('to', toDate.toISOString());

            const response = await fetch(`${baseUrl}/api/TokenHistory/user/${currentVenueKey}?${params}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                setTokenHistory(data);
            }
        } catch (error) {
            console.error('Error fetching token history:', error);
        } finally {
            setLoading(false);
        }
    }, [currentVenueKey, fromDate, toDate]);

    const applyFilters = useCallback( () => {
        let filtered = [...tokenHistory];

        // Filter by category
        if (selectedCategory !== 'all') {
            filtered = filtered.filter(t => t.categoryName === selectedCategory);
        }

        setFilteredData(filtered);
    }, [tokenHistory, selectedCategory]);
    
    useEffect(() => {
        const venueKey = Helper.getUserVenueId();
        venueKey.then(key => {
            setCurrentVenueKey(key);
        });
    }, []);

    useEffect(() => {
        if (currentVenueKey) {
            fetchTokenHistory();
        }
    }, [currentVenueKey, fromDate, toDate, fetchTokenHistory]);

    useEffect(() => {
        applyFilters();
    }, [applyFilters]);

    const formatTokenChange = (change) => {
        if (change > 0) {
            return <span className="text-green-400">+{change}</span>;
        } else if (change < 0) {
            return <span className="text-red-400">{change}</span>;
        }
        return <span className="text-gray-400">{change}</span>;
    };

    const getCategoryBadge = (categoryName) => {
        const colorClass = categoryColors[categoryName] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
        return (
            <span className={`px-2 py-1 rounded text-xs font-medium border ${colorClass}`}>
                {categoryName}
            </span>
        );
    };


    if (loading) {
        return (
            <LayoutWrapper title="Transaction History">
                <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
                </div>
            </LayoutWrapper>
        );
    }

    return (
        <LayoutWrapper
            title="Transaction History"
            subtitle="View your token transactions and payment history"
        >
            <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="card p-6 bg-gradient-to-br from-emerald-900/30 to-gray-900">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-300 text-sm font-medium">Total Purchased</p>
                                <p className="text-2xl font-bold text-emerald-400 mt-1">
                                    {tokenHistory.filter(t => t.category === TokenHistoryCategory.Purchase).reduce((sum, t) => sum + t.tokenChange, 0)}
                                </p>
                            </div>
                            <BanknotesIcon className="h-10 w-10 text-emerald-400 opacity-50" />
                        </div>
                    </div>
                    <div className="card p-6 bg-gradient-to-br from-sky-900/30 to-gray-900">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-300 text-sm font-medium">Spent on Sessions</p>
                                <p className="text-2xl font-bold text-sky-400 mt-1">
                                    {Math.abs(tokenHistory.filter(t => t.category === TokenHistoryCategory.SessionBooking).reduce((sum, t) => sum + t.tokenChange, 0))}
                                </p>
                            </div>
                            <SparklesIcon className="h-10 w-10 text-sky-400 opacity-50" />
                        </div>
                    </div>
                    <div className="card p-6 bg-gradient-to-br from-amber-900/30 to-gray-900">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-300 text-sm font-medium">Cancelled Sessions</p>
                                <p className="text-2xl font-bold text-amber-400 mt-1">
                                    {tokenHistory.filter(t => t.category === TokenHistoryCategory.SessionCancellation).reduce((sum, t) => sum + t.tokenChange, 0)}
                                </p>
                            </div>
                            <CheckCircleIcon className="h-10 w-10 text-amber-400 opacity-50" />
                        </div>
                    </div>
                    <div className="card p-6 bg-gradient-to-br from-violet-900/30 to-gray-900">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-300 text-sm font-medium">Subscription Tokens</p>
                                <p className="text-2xl font-bold text-violet-400 mt-1">
                                    {tokenHistory.filter(t => t.category === TokenHistoryCategory.SubscriptionAllocation).reduce((sum, t) => sum + t.tokenChange, 0)}
                                </p>
                            </div>
                            <ArrowPathIcon className="h-10 w-10 text-violet-400 opacity-50" />
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="card p-6">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className="btn-outline flex items-center gap-2"
                        >
                            <FunnelIcon className="h-5 w-5" />
                            {showFilters ? 'Hide Filters' : 'Show Filters'}
                        </button>

                        {showFilters && (
                            <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Category
                                    </label>
                                    <select
                                        value={selectedCategory}
                                        onChange={(e) => setSelectedCategory(e.target.value)}
                                        className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="all">All Categories</option>
                                        {categories.map(cat => (
                                            <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        From Date
                                    </label>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            value={fromDate}
                                            onChange={setFromDate}
                                            format="DD/MM/YYYY"
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    variant: "outlined",
                                                    size: "small"
                                                }
                                            }}
                                        />
                                    </LocalizationProvider>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        To Date
                                    </label>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            value={toDate}
                                            onChange={setToDate}
                                            format="DD/MM/YYYY"
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    variant: "outlined",
                                                    size: "small"
                                                }
                                            }}
                                        />
                                    </LocalizationProvider>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Transaction Table */}
                <div className="card overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-700">
                            <thead className="bg-gray-800">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                        Date & Time
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                        Category
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                        Description
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                                        Tokens
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700">
                                {filteredData.length === 0 ? (
                                    <tr>
                                        <td colSpan="4" className="px-6 py-12 text-center text-gray-400">
                                            No transactions found for the selected period
                                        </td>
                                    </tr>
                                ) : (
                                    filteredData.map((transaction, index) => (
                                        <tr key={transaction.id} className="hover:bg-gray-800/50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center text-sm">
                                                    <ClockIcon className="h-4 w-4 text-gray-500 mr-2" />
                                                    <div>
                                                        <div className="text-white">
                                                            {new Date(transaction.createdOn).toLocaleDateString()}
                                                        </div>
                                                        <div className="text-gray-500 text-xs">
                                                            {new Date(transaction.createdOn).toLocaleTimeString()}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {getCategoryBadge(transaction.categoryName)}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm text-white">
                                                    {transaction.notes || transaction.categoryName}
                                                </div>
                                                {transaction.referenceId && (
                                                    <div className="text-xs text-gray-500">
                                                        Ref: #{transaction.referenceId}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                {formatTokenChange(transaction.tokenChange)}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Legend */}
                <div className="card p-6">
                    <h3 className="text-white font-semibold mb-4">Transaction Types</h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                            <span className="text-sm text-gray-300">Token Purchase</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-sky-500"></div>
                            <span className="text-sm text-gray-300">Session Booking</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                            <span className="text-sm text-gray-300">Session Cancelled</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-violet-500"></div>
                            <span className="text-sm text-gray-300">Subscription</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-rose-500"></div>
                            <span className="text-sm text-gray-300">Manual Adjustment</span>
                        </div>
                    </div>
                </div>
            </div>
        </LayoutWrapper>
    );
};

export default TransactionHistory;

